import { kebabCase, startCase } from 'lodash';
import { useCallback, useMemo, VFC } from 'react';

import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import ReactSelect from 'dashboard/components/ReactSelect';
import ParamsLegacyDynamicFlowUpdateFields from 'dashboard/components/WorkflowControlPanel/StepConfigAction/ParamsLegacyDynamicFlowUpdateFields';
import useWorkflowError from 'dashboard/components/WorkflowControlPanel/useWorkflowError';
import {
  filterUsableObjects,
  generateJsonSchemaConfig,
  UsableObjectConfig,
} from 'dashboard/lib/workflows';
import { ObjectPropertiesSchema } from 'dashboard/models/external-integration';
import { WorkflowError, WorkflowFieldObjectKey } from 'dashboard/models/workflow';
import {
  WorkflowStepActionKey,
  WorkflowUpdateInquiryFromVerificationAction,
} from 'dashboard/models/workflow-version';
import Spacer from 'design-system/components/Spacer';
import { JsonSchema, JsonSchemaTypes } from 'lib/json-schema';

export interface ParamsUpdateInquiryFieldsFromVerificationProps {
  action: WorkflowUpdateInquiryFromVerificationAction;
  usableObjects: UsableObjectConfig[];
  errors: WorkflowError[];
  editable: boolean;
  updateActionParams: (newParams: WorkflowUpdateInquiryFromVerificationAction['params']) => void;
}

const ParamsUpdateInquiryFieldsFromVerification: VFC<
  ParamsUpdateInquiryFieldsFromVerificationProps
> = ({ action, usableObjects, errors, editable, updateActionParams }) => {
  const { params } = action;
  const permittedObjects = useMemo<WorkflowFieldObjectKey[]>(
    () =>
      action.key === WorkflowStepActionKey.UpdateInquiryFieldsFromVerificationDatabase
        ? [WorkflowFieldObjectKey.VerificationDatabase]
        : [WorkflowFieldObjectKey.VerificationGovernmentId],
    [action],
  );

  // return only the verifications, omit flow state
  const possibleObjects = useMemo(() => {
    return filterUsableObjects({
      usableObjects,
      options: {
        permittedObjects,
        excludeNonPersonaObjects: true,
      },
    }).filter((object) => object.step != null);
  }, [usableObjects, permittedObjects]);

  // generating inquiry field options from WorkflowSchema
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const inquiryFieldOptions: ObjectPropertiesSchema[] = useMemo(() => {
    const inquiryObjects = filterUsableObjects({
      usableObjects,
      options: {
        permittedObjects: [...permittedObjects, WorkflowFieldObjectKey.Inquiry],
      },
    }).filter((object) => object.step != null);

    const jsonFieldSchema = generateJsonSchemaConfig({
      usableObjects: inquiryObjects,
    });

    const inquiryFieldSchemaArray = Object.values(jsonFieldSchema.jsonSchema?.properties);

    if (inquiryFieldSchemaArray.length > 0) {
      return Object.entries(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        (inquiryFieldSchemaArray[0].properties?.inquiry?.properties?.fields?.properties ??
          {}) as JsonSchema,
      )
        .filter(([_, opt]) => opt.type === JsonSchemaTypes.String)
        .map(([value, opt]) => {
          return {
            label: startCase(value),
            value: kebabCase(value),
            description: opt.description,
            objectType: opt.type,
          } as ObjectPropertiesSchema;
        });
    }
  }, [usableObjects, permittedObjects]);

  const updateVerificationParam = useCallback(
    (verification: string) =>
      updateActionParams({
        ...params,
        verification,
      }),
    [params, updateActionParams],
  );

  const { hasErrorFor, renderErrorMessageFor } = useWorkflowError(errors);

  return (
    <>
      <Spacer height={2} />
      <FormControlLabel htmlFor='params-update-inquiry-fields__verification-input'>
        Verification
      </FormControlLabel>
      <ReactSelect
        value={params['verification']}
        options={possibleObjects}
        onChange={updateVerificationParam}
        id='params-update-inquiry-fields__verification'
        hasError={hasErrorFor('verification')}
        disabled={!editable || possibleObjects.length === 0}
      />
      {renderErrorMessageFor('verification')}
      {params['verification'] && (
        <ParamsLegacyDynamicFlowUpdateFields
          title='Inquiry Fields'
          fieldsParamKey='inquiry-to-verification-field-mapping'
          fieldMapping={action.params['inquiry-to-verification-field-mapping']}
          usableObjects={usableObjects}
          permittedObjects={permittedObjects}
          mapsFromInquiryFields={false}
          errors={errors}
          editable={editable}
          updateFieldMapping={(fieldMapping) =>
            updateActionParams({
              ...action.params,
              ...fieldMapping,
            })
          }
          panelTitle='Configure the mapping of Inquiry fields (left) to Verification fields (right)'
          leftFieldOptions={inquiryFieldOptions}
          verificationStepToken={params['verification']}
        />
      )}
    </>
  );
};

export default ParamsUpdateInquiryFieldsFromVerification;
